import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2, Lock } from "lucide-react";
import type { Permission } from "@bruhat/shared";
import { cn } from "@/lib/cn";
import { usePermissions } from "@/hooks/use-permissions";
import { explainPermission } from "@/hooks/use-permission-label";

type Variant =
  "primary" | "secondary" | "ghost" | "danger" | "accent" | "outline";
type Size = "sm" | "md" | "lg" | "icon";

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 disabled:bg-brand-300",
  secondary:
    "bg-surface-sunken text-ink hover:bg-surface-border active:bg-slate-300 dark:bg-night-sunken dark:text-slate-100 dark:hover:bg-night-border",
  outline:
    "border border-surface-border bg-white text-ink hover:bg-surface-muted dark:border-night-border dark:bg-transparent dark:text-slate-100 dark:hover:bg-night-sunken",
  ghost:
    "text-ink-muted hover:bg-surface-sunken hover:text-ink dark:text-slate-400 dark:hover:bg-night-sunken dark:hover:text-slate-100",
  danger:
    "bg-negative text-white hover:bg-red-700 active:bg-red-800 disabled:bg-red-300",
  accent: "bg-accent-300 text-ink hover:bg-accent-400 active:bg-accent-500",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 px-3 text-xs gap-1.5",
  md: "h-9 px-4 text-sm gap-2",
  lg: "h-11 px-6 text-sm gap-2",
  icon: "h-9 w-9 justify-center",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  /**
   * Permission the action needs. Without it the button is shown DISABLED with a
   * tooltip naming the roles that do have it — never hidden. A vanished button
   * reads as a missing feature; a disabled one reads as "ask your admin".
   */
  requires?: Permission;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant = "primary",
      size = "md",
      loading,
      icon,
      children,
      disabled,
      requires,
      title,
      ...props
    },
    ref,
  ) {
    const { can } = usePermissions();
    const denied = requires ? !can(requires) : false;

    return (
      <button
        ref={ref}
        disabled={disabled || loading || denied}
        title={denied && requires ? explainPermission(requires) : title}
        aria-disabled={denied || undefined}
        className={cn(
          "inline-flex items-center rounded-lg font-medium transition-colors",
          "disabled:cursor-not-allowed disabled:opacity-60",
          VARIANTS[variant],
          SIZES[size],
          className,
        )}
        {...props}
      >
        {denied ? (
          <Lock className="h-3.5 w-3.5" />
        ) : loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          icon
        )}
        {children}
      </button>
    );
  },
);
